import { FC, useEffect, useState } from "react";
import { Button, Container, TableContainerChargeback } from "../styles";
import { T2 } from "../../../styles/titles";
import { PlusOutlined } from "@ant-design/icons";
import { FormFilter } from "./components/filter/filters";
import { LiveTable } from "./components/liveTable/liveTable";
import { ModalCadastroChargeback } from "./components/modalCadastroChargeback/modalCadastroChargeback";
import LoadingOverlay from "../../../components/LoadingOverlay";
import ChargebackService from "./service/chargebackService";
import {
  ChargebackTableData,
  ChargebackFilter,
  ChargebackItem,
} from "./types";
import useCurrentUser from "../../../hooks/useCurrentUser";
import {createHTMLElement, downloadFile} from "../../../utils/downloadFiles";
import {toast} from "react-toastify";
import { can, PortalPermissions } from "../../../config/permissions";

const Chargeback: FC = () => {
  const chargebackService = new ChargebackService();
  const user = useCurrentUser();
  const isAdmin = user?.userType === 3;
  const isReadOnly = user?.userType === 6 || user?.userType === 7;
  const userEmail = isReadOnly ? (user?.email ?? null) : null;
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<ChargebackTableData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterData, setFilterData] = useState<ChargebackFilter | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedChargeback, setSelectedChargeback] = useState<ChargebackItem | undefined>(undefined);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [getSaveChanges, setGetSaveChanges] = useState<(() => any) | null>(null);

  // Buscar dados e atualizar tabela
  const fetchData = async () => {
    if (!filterData) return;

    setIsLoading(true);
    try {
      const data = await chargebackService.getChargebacks(currentPage, pageSize, filterData, userEmail);
      setTableData({
        records: data.records || [],
        count: data.rowCount || 0,
      });
      return data;
    } catch (error) {
      console.error("Erro ao buscar chargebacks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setSelectedChargeback(undefined);
    setMode("create");
    setOpenModal(true);
  };

  const handleOpenViewModal = (record: ChargebackItem) => {
    setSelectedChargeback(record);
    setMode("view");
    setOpenModal(true);
  };

  const handleEdit = (record: ChargebackItem) => {
    setSelectedChargeback(record);
    setMode("edit");
    setOpenModal(true);
  };

  const handlePdfDownload = async (record: ChargebackItem) => {
    try {
      setIsLoading(true);
      setSelectedChargeback(record);

      const blob  = await chargebackService.downloadPdf(record);

      const htmlElement = createHTMLElement(blob);
      const fileName = `chargeback_${record.merchantId}_${record.nsu}`;
      downloadFile(htmlElement, fileName, 'pdf');

      toast.success('PDF baixado com sucesso!');
    } catch (error: any) {
      if (error.response?.status === 404) {
        toast.error('PDF não encontrado para este chargeback');
      }
      else {
        toast.error('Erro ao baixar o PDF. Tente novamente.');
      }
      console.log(error);
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleSaveChanges = async (modifications: any) => {
    try {
      setIsLoading(true);
      return await chargebackService.updateRefundStatus(modifications);
    }
    catch (error) {
      console.log(error);
    }
    finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (filterData) setCurrentPage(1);
  }, [filterData]);

  useEffect(() => {
    if (filterData) {
      fetchData();
    }
  }, [filterData, currentPage, pageSize]);

  return (
    <Container>
      <LoadingOverlay show={isLoading} relative />
      <T2>Chargebacks</T2>
    {isAdmin && !isReadOnly && can(PortalPermissions.cobrancas.chargebacks.contestar) && (
      <Button
        icon={<PlusOutlined />}
        size="small"
        style={{ width: "12rem", height: 32, marginLeft: "auto", display: "block" }}
        onClick={handleOpenCreateModal}
      >
        Contestação
      </Button>
    )}

      <ModalCadastroChargeback
        open={openModal}
        initialData={selectedChargeback}
        mode={mode}
        onClose={() => {
          setOpenModal(false);
          setSelectedChargeback(undefined);
          setMode("create");
        }}
        onCreate={async (data) => {
          await chargebackService.createChargeback(data);
          await fetchData();
        }}
        onUpdate={async (payload) => {
          if (!selectedChargeback) return;
          await chargebackService.updateChargeback(selectedChargeback.id, payload);
          await fetchData();
        }}
      />

      <FormFilter getFilters={(data) => setFilterData(data)} email={userEmail} />

      {tableData && (
        <TableContainerChargeback>
          <LiveTable
            data={tableData}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            onView={handleOpenViewModal}
            onEdit={handleEdit}
            onDownload={handlePdfDownload}
            onSaveChanges={(callback) => setGetSaveChanges(() => callback)}
          />
          {!isReadOnly && can(PortalPermissions.cobrancas.chargebacks.estornar) && (
            <Button
             style={{ width: "12rem", height: 32, marginLeft: "auto", display: "block" , marginTop:"1rem"}}
             size="small"
              onClick={async () => {
                if (getSaveChanges) {
                  const modifications = getSaveChanges();
                  const response: any = await handleSaveChanges(modifications);
                  if (!response.success) toast.error(response.message as string);
                  else {
                    toast.success(response.message as string);
                    await fetchData();
                  }
                }
              }}
            >
              Salvar estornos
            </Button>
          )}
        </TableContainerChargeback>
      )}
    </Container>
  );
};

export default Chargeback;
